import {
  ReloginContext,
  setOnUnauthorized,
  setTokenRefresher,
  setTokenValidator,
} from "@inrange/building-manager-api-client";
import {
  KINDE_ENVIRONMENT,
  makeEnsureFreshToken,
  makeTokenValidator,
  useContextTS,
  useRejectForeignTokenOnInitialLoad,
} from "@inrange/shared-components";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import * as Sentry from "@sentry/react";
import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Modal } from "react-bootstrap";
import Loading from "../routes/Loading";

interface User {
  name: string;
  email: string;
  family_name: string;
  given_name: string;
  picture: string;
  org_id?: string;
}

interface UserContextValue {
  user?: User;
  logout?: () => void;
}

const UserContext = createContext<UserContextValue>({});

const UserProvider = ({ children }: { children: ReactNode }) => {
  const {
    login,
    logout,
    isLoading: isAuthLoading,
    user: kindeUser,
    getOrganization,
    getToken,
    refreshToken,
  } = useKindeAuth();

  const [user, setUser] = useState<User | undefined>();
  const [kindeOrgCode, setKindeOrgCode] = useState<string | null | undefined>(
    undefined
  );
  const [showReloginModal, setShowReloginModal] = useState(false);

  // Shared relogin promise
  const reloginPromiseRef = useRef<Promise<void> | null>(null);
  const reloginResolveRef = useRef<(() => void) | null>(null);

  // -- Sync user info into context + Sentry
  useEffect(() => {
    if (!isAuthLoading && kindeUser) {
      setUser({
        name: `${kindeUser.givenName} ${kindeUser.familyName}`,
        email: kindeUser.email!,
        family_name: kindeUser.familyName!,
        given_name: kindeUser.givenName!,
        picture: kindeUser.picture!,
      });
      if (import.meta.env.PROD) {
        Sentry.setUser({ email: kindeUser.email! });
      }
      getOrganization()
        .then(setKindeOrgCode)
        .catch((error) => {
          // v5 getOrganization() is async and can reject (token decode/refresh
          // failure); the sync v3 call could not. Without this, kindeOrgCode
          // stays undefined and the render gate hangs on Loading forever. Treat
          // an unresolvable org as "not inrange" so the logout path below fires.
          Sentry.captureException(error);
          setKindeOrgCode(null);
        });
    }
  }, [isAuthLoading, kindeUser, getOrganization]);

  const { setRelogin } = useContextTS(ReloginContext);

  // --- Relogin function called by API clients ---
  const relogin = useCallback(() => {
    if (!reloginPromiseRef.current) {
      // Show modal once
      console.log("Showing relogin modal");
      setShowReloginModal(true);
      // Create one shared promise
      reloginPromiseRef.current = new Promise<void>((resolve) => {
        reloginResolveRef.current = resolve;
      });
    }
    return reloginPromiseRef.current;
  }, []);

  useEffect(() => {
    setRelogin(() => relogin);
  }, [relogin, setRelogin]);

  // Wire axios response 401 interceptor to trigger relogin. Without this, a
  // signed-out user's inflight React Query polls (e.g. useSitePR) keep firing
  // 401s silently instead of prompting the user to re-auth (see CUSTOMER-APP-P
  // and the ADMIN-APP-8D-style admin 401s).
  useEffect(() => {
    setOnUnauthorized(relogin);
    return () => setOnUnauthorized(null);
  }, [relogin]);

  // Proactively refresh the Kinde access token before each BMA request when
  // it's near expiry. Kinde v5's getToken() no longer refreshes on its own, so
  // without this an access token that expires while this tab stays focused is
  // never renewed and every request 401s until a full reload (see
  // ensureFreshKindeToken + getAuthTokenBlocking).
  useEffect(() => {
    setTokenRefresher(
      makeEnsureFreshToken(
        getToken,
        refreshToken,
        KINDE_ENVIRONMENT.KINDE_ADMIN_APP_CLIENT_ID
      )
    );
    return () => setTokenRefresher(null);
  }, [getToken, refreshToken]);

  // Gate API calls on token *validity*, not just presence. Kinde v5's getToken()
  // returns a present-but-invalid token (expired, or - via a stale bleed - minted
  // by another client) without clearing it, which would 401-loop behind the
  // relogin modal instead of blocking on it. The validator lets
  // getAuthTokenBlocking treat such a token as "no token" and block.
  const validateToken = useMemo(
    () => makeTokenValidator(KINDE_ENVIRONMENT.KINDE_ADMIN_APP_CLIENT_ID),
    []
  );
  useEffect(() => {
    setTokenValidator(validateToken);
    return () => setTokenValidator(null);
  }, [validateToken]);

  // Reject a foreign-client token on cold load and re-authenticate (restores the
  // v3 SDK behaviour the v5 upgrade dropped - see the hook for the full story).
  // A full interactive login is safe here because the first token resolves while
  // we're still on the Loading screen, so there's no user state to lose; it mints
  // our own client's token off the SSO session and re-binds the shared refresh
  // cookie to us. A wrong-azp token appearing later is left to the relogin modal.
  const triggerReauth = useCallback(() => {
    login({
      orgCode: KINDE_ENVIRONMENT.KINDE_INRANGE_ORG_CODE,
      state: { redirectTo: window.location.pathname + window.location.search },
    });
  }, [login]);
  const initialAuthOk = useRejectForeignTokenOnInitialLoad({
    ready: !isAuthLoading && !!kindeUser,
    getToken,
    validateToken,
    triggerReauth,
  });

  // --- Detect when user returns and token is valid again ---
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === "visible" && showReloginModal) {
        try {
          const token = await getToken();
          // Only clear the modal once a genuinely usable token is present -
          // matching the gate in getAuthTokenBlocking. A present-but-invalid
          // token (e.g. the user switched back without actually re-logging-in)
          // must keep the modal up rather than unblock into another 401.
          if (token && validateToken(token)) {
            setShowReloginModal(false);
            console.log("Relogin complete - unblocking requests");
            reloginResolveRef.current?.();
            reloginResolveRef.current = null;
            reloginPromiseRef.current = null;
          }
        } catch {
          // still not logged in — ignore
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [getToken, showReloginModal, validateToken]);

  // --- Initial auth flow ---
  if (!isAuthLoading && !kindeUser) {
    login({
      orgCode: KINDE_ENVIRONMENT.KINDE_INRANGE_ORG_CODE,
      state: { redirectTo: window.location.pathname + window.location.search },
    });
  }

  if (!user || kindeOrgCode === undefined || !initialAuthOk) {
    return <Loading label="Loading ..." />;
  }

  if (
    kindeOrgCode !== KINDE_ENVIRONMENT.KINDE_INRANGE_ORG_CODE ||
    !user.email.endsWith("@inrange.io")
  ) {
    setTimeout(() => {
      console.log("Fallback logout");
      logout();
    }, 100);
    return <Loading label="Loading ..." />;
  }

  return (
    <UserContext.Provider value={{ user, logout }}>
      {children}

      {/* Relogin Modal */}
      <Modal
        show={showReloginModal}
        backdrop="static"
        keyboard={false}
        centered
        style={{ zIndex: 20000 }} // ensure it’s above all other modals
      >
        <Modal.Header>
          <Modal.Title>Session expired</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Your login session has expired. To continue, please log in again in
            a new tab. You can safely keep this page open — any unsaved changes
            will remain here.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={() => {
              // noopener: without it the Kinde v5 provider closes this new tab
              // on load (it treats a same-origin opener as a stale auth popup),
              // which would defeat the whole "log in in a new tab" flow. The
              // relogin handshake works via shared localStorage, not the opener.
              window.open(window.location.origin, "_blank", "noopener,noreferrer");
            }}
          >
            Login in new tab
          </Button>
        </Modal.Footer>
      </Modal>
    </UserContext.Provider>
  );
};

export { UserContext, UserProvider };
